<script setup lang="ts">
import { useSelectedLogSorterStore } from '../stores/selectedLogSorterStore';
import { storeToRefs } from 'pinia';
import UpDownInputNumber from '@/common/components/UpDownInputNumber.vue';
import { Select, Fieldset, InputText, ToggleSwitch, Message, MultiSelect, InputNumber } from 'primevue';
import { LogSorterNumberSequence } from 'shared/logSorter/domain/logSorterNumberSequence';
import { LogSorterColumnPlacement } from 'shared/logSorter/domain/logSorterBinLayout';
import { computed } from 'vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useSelectedLogSorterStore();
const storageAreaStore = useStorageAreaStore();

const {
  name,
  nameChanged,
  nameInvalid,
  intakes,
  intakesChanged,
  addLogWhenSorted,
  addLogWhenSortedChanged,
  longitude,
  longitudeChanged,
  longitudeInvalid,
  latitude,
  latitudeChanged,
  latitudeInvalid,
  heading,
  headingChanged,
  numberSequence,
  numberSequenceChanged,
  numberOfBinsCol1,
  numberOfBinsCol1Changed,
  numberOfBinsCol2,
  numberOfBinsCol2Changed,
  binLayout,
  binLayoutChanged,
  binDefaultWidth,
  binWidthChanged,
  binDefaultLength,
  binLengthChanged,
  columnGap,
  columnGapChanged,
  rowGap,
  rowGapChanged,
  geofenceTop,
  geofenceTopChanged,
  geofenceBottom,
  geofenceBottomChanged,
  geofenceOutside,
  geofenceOutsideChanged,
  geofenceInside,
  geofenceInsideChanged
} = storeToRefs(store);

const intakeOptions = computed(() => storageAreaStore.storageAreas
  .filter(s => s.type === StorageAreaType.LogSorterIntake)
  .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })))

const numberSequenceOptions: NumberSequenceOption[] = [
  LogSorterNumberSequence.Sequential,
  LogSorterNumberSequence.SequentialMirrored,
  LogSorterNumberSequence.Alternating,
  LogSorterNumberSequence.AlternatingMirrored
].map(v => {
  return {
    text: translations.value.enums.logSorterNumberSequence(v),
    value: v
  };
});

const binLayoutOptions: BinLayoutOptions[] = [
  LogSorterColumnPlacement.Left,
  LogSorterColumnPlacement.Right,
].map(v => {
  return {
    text: translations.value.enums.logSorterBinLayout(v),
    value: v
  };
});

interface NumberSequenceOption {
  text: string;
  value: LogSorterNumberSequence;
}

interface BinLayoutOptions {
  text: string;
  value: LogSorterColumnPlacement;
}

</script>

<template>
  <div class="logsorter-panel">

    <div class="field name-field" :class="{ changed: nameChanged }">
      <label>{{ translations.generic.name }}</label>
      <InputText autofocus v-model:model-value="name">
      </InputText>
      <Message v-if="nameInvalid" severity="error" variant="simple">{{ nameInvalid }}</Message>
    </div>

    <div class="field intakes-field" :class="{ changed: intakesChanged }">
      <label>{{ translations.logSorter.panel.intakes }}</label>
      <MultiSelect v-model:model-value="intakes" :options="intakeOptions" option-value="id" option-label="name">
      </MultiSelect>
    </div>

    <div class="toggle-field add-log-when-sorted-field" :class="{ changed: addLogWhenSortedChanged }">
      <ToggleSwitch v-model:modelValue="addLogWhenSorted"></ToggleSwitch>
      <label for="storage-area-drawer-automatic-product-change">{{ translations.addLogSorterDialog.addLogWhenSorted
      }}</label>
    </div>

    <div class="field" :class="{ changed: longitudeChanged }">
      <label>{{ translations.generic.longitude }}</label>
      <InputNumber fluid v-model:model-value="longitude" :min-fraction-digits="8">
      </InputNumber>
      <Message v-if="longitudeInvalid" severity="error" variant="simple">{{ longitudeInvalid }}</Message>
    </div>
    <div class="field" :class="{ changed: latitudeChanged }">
      <label>{{ translations.generic.latitude }}</label>
      <InputNumber fluid v-model:model-value="latitude" :min-fraction-digits="8">
      </InputNumber>
      <Message v-if="latitudeInvalid" severity="error" variant="simple">{{ latitudeInvalid }}</Message>
    </div>
    <div class="field" :class="{ changed: headingChanged }">
      <label>{{ translations.generic.direction }}</label>
      <UpDownInputNumber v-model:model-value="heading">
      </UpDownInputNumber>
    </div>
    <div class="field" :class="{ changed: numberSequenceChanged }">
      <label>{{ translations.logSorter.panel.numberSequence }}</label>
      <Select v-model:model-value="numberSequence" :options="numberSequenceOptions" option-label="text"
        option-value="value">
      </Select>
    </div>
    <div class="bin-row">
      <div class="field" :class="{ changed: numberOfBinsCol1Changed }">
        <label>{{ translations.logSorter.panel.numberOfBinsCol1 }}</label>
        <UpDownInputNumber v-model:model-value="numberOfBinsCol1" :min="0">
        </UpDownInputNumber>
      </div>
      <div class="field" :class="{ changed: numberOfBinsCol2Changed }">
        <label>{{ translations.logSorter.panel.numberOfBinsCol2 }}</label>
        <UpDownInputNumber v-model:model-value="numberOfBinsCol2" :min="0">
        </UpDownInputNumber>
      </div>
      <div class="field" :class="{ changed: binLayoutChanged }">
        <label>{{ translations.logSorter.panel.binLayout }}</label>
        <Select v-model:model-value="binLayout" :options="binLayoutOptions" option-label="text" option-value="value">
        </Select>
      </div>
    </div>
    <div class="field" :class="{ changed: binWidthChanged }">
      <label>{{ translations.logSorter.panel.binWidth }}</label>
      <UpDownInputNumber v-model:model-value="binDefaultWidth" :min="0" @update:model-value="store.onBinChanged(false)">
      </UpDownInputNumber>
    </div>
    <div class="field" :class="{ changed: binLengthChanged }">
      <label>{{ translations.logSorter.panel.binLength }}</label>
      <UpDownInputNumber v-model:model-value="binDefaultLength" :min="0" @update:model-value="store.onBinChanged(false)">
      </UpDownInputNumber>
    </div>
    <div class="field" :class="{ changed: columnGapChanged }">
      <label>{{ translations.logSorter.panel.columnGap }}</label>
      <UpDownInputNumber v-model:model-value="columnGap" :min="0" @update:model-value="store.onBinChanged(false)">
      </UpDownInputNumber>
    </div>
    <div class="field" :class="{ changed: rowGapChanged }">
      <label>{{ translations.logSorter.panel.rowGap }}</label>
      <UpDownInputNumber v-model:model-value="rowGap" :min="0" @update:model-value="store.onBinChanged(false)">
      </UpDownInputNumber>
    </div>

    <Fieldset class="geofence-fields" :legend="translations.shapes.geofenceFieldSet">
      <div class="geofence-field">
        <div class="field" :class="{ changed: geofenceTopChanged }">
          <label>{{ translations.generic.top }}</label>
          <UpDownInputNumber v-model:model-value="geofenceTop">
          </UpDownInputNumber>
        </div>

        <div class="field" :class="{ changed: geofenceBottomChanged }">
          <label>{{ translations.generic.bottom }}</label>
          <UpDownInputNumber v-model:model-value="geofenceBottom">
          </UpDownInputNumber>
        </div>

        <div class="field" :class="{ changed: geofenceInsideChanged }">
          <label>{{ translations.generic.inside }}</label>
          <UpDownInputNumber v-model:model-value="geofenceInside">
          </UpDownInputNumber>
        </div>

        <div class="field" :class="{ changed: geofenceOutsideChanged }">
          <label>{{ translations.generic.outside }}</label>
          <UpDownInputNumber v-model:model-value="geofenceOutside">
          </UpDownInputNumber>
        </div>
      </div>
    </Fieldset>

  </div>
</template>

<style scoped>
.logsorter-panel {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
  overflow-y: auto;
}

.name-field,
.intakes-field,
.geofence-fields,
.add-log-when-sorted-field {
  grid-column: 1/3;
}

.geofence-field {
  grid-column: 1/3;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.bin-row {
  grid-column: 1 / 3;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}
</style>
